<script setup lang="ts">
import { PaymentIntent } from '@stripe/stripe-js/dist/api'
import {
  Appearance,
  Stripe,
  StripeElements,
  StripeLinkAuthenticationElement,
  StripePaymentElement,
  StripePaymentElementOptions,
} from '@stripe/stripe-js/dist/stripe-js'
import { useMutation } from '@tanstack/vue-query'
import DataStateLoading from 'pejer/src/components/DataState/DataStateLoading.vue'
import { cn } from 'pejer/src/lib/utils'
import { ErrorObject } from 'pejer/src/types/types'
import { computed, ref, watch } from 'vue'
import PayButton from '../PayButton.vue'

const props = defineProps<{
  stripe: Stripe | null
  clientSecret: string
  paymentReferenceID: string
  grossAmount: number
  currency: string
  customer: {
    name: string | null
    email: string | null
    mobile: string | null
  }
  successUrl: string
  failedUrl: string
}>()

const emits = defineEmits<{
  (e: 'onPaidSuccess', value: PaymentIntent): void
}>()

const elements = ref<StripeElements | null>(null)
const emailAddress = ref<string | null>(null)

const linkAuthenticationHTMLElement = ref<HTMLElement | null>(null)
const paymentElementHTMLElement = ref<HTMLElement | null>(null)

const stripeReadyStates = ref<Record<string, boolean>>({
  linkAuthentication: false,
  paymentElement: false,
})
const stripeReady = computed(() => {
  return Object.values(stripeReadyStates.value).every((state) => state === true)
})
const stripePartialReady = computed(() => {
  return Object.values(stripeReadyStates.value).some((state) => state === true)
})

const maxHeightClass = computed(() => {
  if (stripeReady.value) return 'max-h-[100%]'

  return 'max-h-[10000px]'
})

const paymentElement = ref<StripePaymentElement | null>(null)
const linkAuthenticationElement = ref<StripeLinkAuthenticationElement | null>(null)

watch(
  () => linkAuthenticationHTMLElement.value,
  () => {
    linkAuthenticationElement.value?.mount(linkAuthenticationHTMLElement.value)
  }
)
watch(
  () => paymentElementHTMLElement.value,
  () => {
    paymentElement.value?.mount(paymentElementHTMLElement.value)
  }
)

watch(
  () => props.stripe,
  () => {
    initializeForm()
  }
)
initializeForm()

function mountLinkAuth() {
  linkAuthenticationElement.value = elements.value.create('linkAuthentication')

  linkAuthenticationElement.value.on('change', (event) => {
    emailAddress.value = event.value.email
  })

  linkAuthenticationElement.value.on('ready', async () => {
    // await nextTick()
    // await nextTick()
    stripeReadyStates.value = {
      ...stripeReadyStates.value,
      linkAuthentication: true,
    }
  })
  if (!linkAuthenticationHTMLElement.value) return
  linkAuthenticationElement.value.mount(linkAuthenticationHTMLElement.value)
}

function initializeForm() {
  if (!props.stripe) return
  const appearance: Appearance = {
    theme: 'stripe',
  }
  elements.value = props.stripe.elements({ appearance, clientSecret: props.clientSecret })

  mountLinkAuth()

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: {
      type: 'accordion',
    },
    wallets: {
      // We add these direct pay options manually
      applePay: 'never',
      googlePay: 'never',
    },
    defaultValues: {
      billingDetails: {
        name: props.customer.name ?? '',
        email: props.customer.email ?? '',
        phone: props.customer.mobile ?? '',
      },
    },
  }

  paymentElement.value = elements.value.create('payment', paymentElementOptions)
  paymentElement.value.on('ready', async () => {
    stripeReadyStates.value = {
      ...stripeReadyStates.value,
      paymentElement: true,
    }
  })

  if (!paymentElementHTMLElement.value) return
  paymentElement.value.mount(paymentElementHTMLElement.value)
}

const { mutate, error, isError, isPending } = useMutation<PaymentIntent, ErrorObject>({
  mutationFn: async () => {
    let res = await props.stripe?.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/${props.paymentReferenceID}/success`,
        receipt_email: emailAddress.value,
      },
      redirect: 'if_required',
    })

    if (res.error)
      throw {
        error: res.error.message,
        statusCode: 0,
      } as ErrorObject

    return res.paymentIntent
  },
  onSuccess: (data) => {
    emits('onPaidSuccess', data)
  },
})

const dataState = computed(() => {
  return {
    isError: isError,
    error: error,
    isLoading: isPending,
  }
})

async function handleSubmit(event: Event) {
  event.preventDefault()
  if (!props.stripe) return
  if (!elements.value) return
  mutate()
}
</script>
<template>
  <div :class="cn('h-full min-h-[470px] transition-all duration-300', maxHeightClass)">
    <form
      class="w-full flex flex-col gap-2 justify-between h-full min-h-[470px]"
      @submit="handleSubmit"
    >
      <div class="w-full flex flex-col relative">
        <div
          v-if="!stripePartialReady"
          class="w-full flex items-center justify-center absolute z-10 h-[420px]"
        >
          <div class="bg-white/60 backdrop-blur-lg p-2 rounded-full">
            <DataStateLoading :is-loading="true" hide-text :icon-size="32" />
          </div>
        </div>
        <!-- :class="{
          'opacity-0 pointer-events-none': !stripeReady,
        }" -->
        <div class="mb-4" ref="linkAuthenticationHTMLElement">
          <!--Stripe.js injects the Link Authentication Element-->
          test
        </div>
        <div ref="paymentElementHTMLElement">
          <!--Stripe.js injects the Payment Element-->
        </div>
      </div>
      <div>
        <p class="text-sm text-slate-500 mb-2">
          Du vil motta en kvittering på e-post etter at bestillingen er bekreftet.
        </p>
        <PayButton :gross-amount="props.grossAmount" :currency="currency" :data-state="dataState">
          <template #text="{ amount }">
            <!-- <CreditCard class="w-6 h-6" /> -->
            <span>Betal {{ amount }}</span>
          </template>
        </PayButton>
      </div>
    </form>
  </div>
</template>
